import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import { ApiError } from '../coreRoot.reducer'
import {
  CalendarData, CorePageSettings,
  FacebookPage,
  FacebookPost,
  FindGlobalPostsForPostTemplateResponse,
  InstagramPage,
  InstagramPost, LinkedInPage,
  PostTemplate,
  PostTemplatesSearchResult,
  TopicsWithMetadataResponse,
  UserAboSettings, WebResourceMetaData,
} from '@3m5/sm_publish-frontend-api/models'
import {
  findPostTemplatesForCurrentUser,
  getCalendarData,
  getIgPage,
  loadFacebookPage,
  getTopicsAndMetadataForCurrentUser,
  updateAboSettingsForCurrentUser,
  updatePostTemplate,
  getAboSettingsForCurrentUser,
  getCoreFBPageSettings,
  getCoreInstaPageSettings,
  findGlobalPostsForPostTemplate,
  deleteCoreInstaPageSettings,
  getFacebookPost,
  getInstagramPost,
  declineManualDeferredPost,
  publishPost,
  deleteCoreFBPageSettings,
  updateCorePageSettings,
  getMultipleChoiceQuiz,
  getSelectedMultipleChoiceQuiz,
  deleteManualDeferredPost,
  confirmManualDeferredPost,
  deletePost,
  deletePostTemplate,
  retryManualDeferredPost,
  setNetworkContentPage,
  getPageMaster,
  listPageOwnersForPage,
  setUserAsPageMaster,
  setFacebookPage,
  setIGPage,
  getLinkedInPage,
  getUrlInformation,
} from './dashboard.actions'
import { PostingStatus } from '@professocial/professocialPosting/src/shared/types/types'
import { MultipleChoiceQuizDto } from '@professocialApi/professocial-core-frontend-api/models/MultipleChoiceQuizDto'
import { DetailPosts } from '../../shared/types/commonTypes'
import { PageOwner } from '@professocialApi/professocial-core-frontend-api/models'

export interface DashboardState {
  fetching: boolean
  deleteRequestFetching: boolean
  facebookPage?: FacebookPage
  facebookPostsById: { [networkPostId: string]: FacebookPost }
  instagramPage?: InstagramPage
  linkedInPage?: LinkedInPage
  coreFBPageSettings?: CorePageSettings
  coreInstaPageSettings?: CorePageSettings
  reNewPageSettings: boolean
  reloadCalendar: boolean
  instagramPostsById: { [networkPostId: string]: InstagramPost }
  calendarData?: CalendarData
  detailPosts: DetailPosts
  templates?: PostTemplatesSearchResult
  globalPosts?: FindGlobalPostsForPostTemplateResponse
  aboTopics?: TopicsWithMetadataResponse
  aboSettings?: UserAboSettings[]
  updatedPostTemplate?: PostTemplate
  postingStatus?: PostingStatus
  multipleChoiceQuiz?: MultipleChoiceQuizDto
  selectedMultipleChoiceQuiz?: MultipleChoiceQuizDto
  networkContentPage: number
  pageMaster?: PageOwner
  pageOwners?: PageOwner[]
  pageResource?: WebResourceMetaData
  error: ApiError
}

const INITIAL_STATE: DashboardState = {
  fetching: false,
  deleteRequestFetching: false,
  detailPosts: { Facebook: {}, Instagram: {} },
  reloadCalendar: false,
  reNewPageSettings: false,
  networkContentPage: 1,
  facebookPostsById: {},
  instagramPostsById: {},
  error: {},
}

export const DashboardReducer = reducerWithInitialState(INITIAL_STATE)
  .case(setNetworkContentPage, (state, networkContentPage) => {
    return ({ ...state, networkContentPage })
  })
  .case(setFacebookPage, (state, facebookPage) => {
    return ({ ...state, facebookPage })
  })
  .case(setIGPage, (state, instagramPage) => {
    return ({ ...state, instagramPage })
  })
/**
   * loadFacebookPage
   */
  .case(loadFacebookPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.loadFacebookPage
    })
  })

  .case(loadFacebookPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft = {
        ...draft,
        facebookPage: result,
      }
      draft.fetching = false
      return draft
    })
  })

  .case(loadFacebookPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.loadFacebookPage = (error as Response)
    })
  })


/**
   * getIgPage
   */
  .case(getIgPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getIgPage
    })
  })

  .case(getIgPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.instagramPage = { ...result }
      draft.fetching = false
    })
  })

  .case(getIgPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getIgPage = (error as Response)
    })
  })

  /**
   * getLinkedInPage
   */
  .case(getLinkedInPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getLinkedInPage
    })
  })

  .case(getLinkedInPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.linkedInPage = { ...result }
      draft.fetching = false
    })
  })

  .case(getLinkedInPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getLinkedInPage = (error as Response)
    })
  })

/**
   * deleteCoreFBPageSettings
   */
  .case(deleteCoreFBPageSettings, (state) => {
    return ({ ...state, coreFBPageSettings: undefined })
  })

/**
   * getCoreFBPageSettings
   */
  .case(getCoreFBPageSettings.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.coreFBPageSettings = undefined
      delete draft.error.getCoreFBPageSettings
    })
  })

  .case(getCoreFBPageSettings.done, (state, { result }) => {
    return produce(state, draft => {
      draft.coreFBPageSettings = result
      draft.fetching = false
    })
  })

  .case(getCoreFBPageSettings.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getCoreFBPageSettings = (error as Response)
    })
  })

/**
   * deleteCoreInstaPageSettings
   */
  .case(deleteCoreInstaPageSettings, (state) => {
    return ({ ...state, coreInstaPageSettings: undefined })
  })

/**
   * getCoreInstaPageSettings
   */
  .case(getCoreInstaPageSettings.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.coreInstaPageSettings = undefined
      delete draft.error.getCoreFBPageSettings
    })
  })

  .case(getCoreInstaPageSettings.done, (state, { result }) => {
    return produce(state, draft => {
      draft.coreInstaPageSettings = result
      draft.fetching = false
    })
  })

  .case(getCoreInstaPageSettings.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getCoreFBPageSettings = (error as Response)
    })
  })

/**
   * getCalendarData
   */
  .case(getCalendarData.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getCalendarData
    })
  })

  .case(getCalendarData.done, (state, { result }) => {
    return produce(state, draft => {
      draft.calendarData = { ...result }
      draft.fetching = false
    })
  })

  .case(getCalendarData.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getCalendarData = (error as Response)
    })
  })

/**
   * findPostTemplatesForCurrentUser
   */
  .case(findPostTemplatesForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.findPostTemplatesForCurrentUser
    })
  })

  .case(findPostTemplatesForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.templates = result
      draft.fetching = false
    })
  })

  .case(findPostTemplatesForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.findPostTemplatesForCurrentUser = (error as Response)
    })
  })

/**
   * getTopicsAndMetadataForCurrentUser
   */
  .case(getTopicsAndMetadataForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getTopicsAndMetadataForCurrentUser
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboTopics = result
      draft.fetching = false
    })
  })

  .case(getTopicsAndMetadataForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getTopicsAndMetadataForCurrentUser = (error as Response)
    })
  })

/**
   * getAboSettingsForCurrentUser
   */
  .case(getAboSettingsForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAboSettingsForCurrentUser
    })
  })

  .case(getAboSettingsForCurrentUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboSettings = result
      draft.fetching = false
    })
  })

  .case(getAboSettingsForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAboSettingsForCurrentUser = (error as Response)
    })
  })

/**
   * updateAboSettingsForCurrentUser
   */
  .case(updateAboSettingsForCurrentUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updateAboSettingsForCurrentUser
    })
  })

  .case(updateAboSettingsForCurrentUser.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(updateAboSettingsForCurrentUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updateAboSettingsForCurrentUser = (error as Response)
    })
  })

/**
   * updatePostTemplate
   */
  .case(updatePostTemplate.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.postingStatus = PostingStatus.pending
      delete draft.error.updatedPostTeplate
    })
  })

  .case(updatePostTemplate.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.postingStatus = PostingStatus.success
      draft.updatedPostTemplate = result
    })
  })

  .case(updatePostTemplate.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.postingStatus = PostingStatus.error
      draft.error.updatedPostTemplate = (error as Response)
    })
  })

/**
   * findGlobalPostsForPostTemplate
   */
  .case(findGlobalPostsForPostTemplate.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.findGlobalPostsForPostTemplate
    })
  })

  .case(findGlobalPostsForPostTemplate.done, (state, { result }) => {
    return produce(state, draft => {
      draft.globalPosts = result
      draft.fetching = false
    })
  })

  .case(findGlobalPostsForPostTemplate.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.findGlobalPostsForPostTemplate = (error as Response)
    })
  })

/**
   * getFacebookPost
   */
  .case(getFacebookPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getFacebookPost
    })
  })

  .case(getFacebookPost.done, (state, { params, result }) => {
    return produce(state, draft => {
      draft.fetching = false
      if (params.networkPostId) {
        draft.facebookPostsById[params.networkPostId] = result
      }
    })
  })

  .case(getFacebookPost.failed, (state, { params, error }) => {
    return produce(state, draft => {
      draft.fetching = false
      if (params.networkPostId) {
        delete draft.facebookPostsById[params.networkPostId]
      }
      draft.error.getFacebookPost = (error as Response)
    })
  })

/**
   * getInstagramPost
   */
  .case(getInstagramPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getInstagramPost
    })
  })

  .case(getInstagramPost.done, (state, { params, result }) => {
    return produce(state, draft => {
      draft.fetching = false
      if (params.networkPostId) {
        draft.instagramPostsById[params.networkPostId] = result
      }
    })
  })

  .case(getInstagramPost.failed, (state, { params, error }) => {
    return produce(state, draft => {
      draft.fetching = false
      if (params.networkPostId) {
        delete draft.instagramPostsById[params.networkPostId]
      }
      draft.error.getInstagramPost = (error as Response)
    })
  })
/**
   * publishPost
   */
  .case(publishPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.postingStatus = PostingStatus.pending
      delete draft.error.publishPost
      draft.reloadCalendar = false
    })
  })

  .case(publishPost.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.postingStatus = PostingStatus.success
      draft.updatedPostTemplate = result
      draft.reloadCalendar = true
    })
  })

  .case(publishPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.postingStatus = PostingStatus.error
      draft.error.publishPost = (error as Response)
    })
  })

/**
   * deletePostTemplate
   */
  .case(deletePostTemplate.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.deleteRequestFetching = true
      delete draft.error.deletePostTemplate
      draft.reloadCalendar = false
    })
  })

  .case(deletePostTemplate.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.deleteRequestFetching = false
      draft.reloadCalendar = true
    })
  })

  .case(deletePostTemplate.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.deleteRequestFetching = false
      draft.error.deletePostTemplate = (error as Response)
    })
  })

/**
   * declineManualDeferredPost
   */
  .case(declineManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.declineManualDeferredPost
      draft.reloadCalendar = false
    })
  })

  .case(declineManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.reloadCalendar = true
    })
  })

  .case(declineManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.declineManualDeferredPost = (error as Response)
    })
  })

/**
   * retryManualDeferredPost
   */
  .case(retryManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.retryManualDeferredPost
      draft.reloadCalendar = false
    })
  })

  .case(retryManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.reloadCalendar = true
    })
  })

  .case(retryManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.retryManualDeferredPost = (error as Response)
    })
  })

/**
   * confirmManualDeferredPost
   */
  .case(confirmManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.confirmManualDeferredPost
      draft.reloadCalendar = false
    })
  })

  .case(confirmManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.reloadCalendar = true
    })
  })

  .case(confirmManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.confirmManualDeferredPost = (error as Response)
    })
  })

/**
   * deletePost
   */
  .case(deletePost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deletePost
      draft.reloadCalendar = false
    })
  })

  .case(deletePost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.reloadCalendar = true
    })
  })

  .case(deletePost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deletePost = (error as Response)
    })
  })

/**
   * deleteManualDeferredPost
   */
  .case(deleteManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deleteManualDeferredPost
      draft.reloadCalendar = false
    })
  })

  .case(deleteManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.reloadCalendar = true
    })
  })

  .case(deleteManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deleteManualDeferredPost = (error as Response)
    })
  })

/**
   * updateCorePageSettings
   */
  .case(updateCorePageSettings.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.reNewPageSettings = false
      delete draft.error.updateCorePageSettings
    })
  })

  .case(updateCorePageSettings.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.reNewPageSettings = true
    })
  })

  .case(updateCorePageSettings.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updateCorePageSettings = (error as Response)
    })
  })

/**
   * getMultipleChoiceQuiz
   */
  .case(getMultipleChoiceQuiz.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getMultipleChoiceQuiz
    })
  })

  .case(getMultipleChoiceQuiz.done, (state, { result }) => {
    return produce(state, draft => {
      draft.multipleChoiceQuiz = result
      draft.fetching = false
    })
  })

  .case(getMultipleChoiceQuiz.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getMultipleChoiceQuiz = (error as Response)
    })
  })

/**
   * getSelectedMultipleChoiceQuiz
   */
  .case(getSelectedMultipleChoiceQuiz.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getMultipleChoiceQuiz
    })
  })

  .case(getSelectedMultipleChoiceQuiz.done, (state, { result }) => {
    return produce(state, draft => {
      draft.selectedMultipleChoiceQuiz = result
      draft.fetching = false
    })
  })

  .case(getSelectedMultipleChoiceQuiz.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getMultipleChoiceQuiz = (error as Response)
    })
  })

/**
   * getPageMaster
   */
  .case(getPageMaster.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getPageMaster
    })
  })

  .case(getPageMaster.done, (state, { result }) => {
    return produce(state, draft => {
      draft.pageMaster = result
      draft.fetching = false
    })
  })

  .case(getPageMaster.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getPageMaster = (error as Response)
    })
  })

/**
   * getPageMaster
   */
  .case(listPageOwnersForPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.listPageOwnersForPage
    })
  })

  .case(listPageOwnersForPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.pageOwners = result
      draft.fetching = false
    })
  })

  .case(listPageOwnersForPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.listPageOwnersForPage = (error as Response)
    })
  })

/**
   * setUserAsPageMaster
   */
  .case(setUserAsPageMaster.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.setCurrentUserAsPageMaster
    })
  })

  .case(setUserAsPageMaster.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })
  .case(setUserAsPageMaster.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.setCurrentUserAsPageMaster = (error as Response)
    })
  })

/**
   * getPageSummary
   */

  .case(getUrlInformation.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getPageSummary
    })
  })

  .case(getUrlInformation.done, (state, { result }) => {
    return produce(state, draft => {
      draft.pageResource = result
      draft.fetching = false
    })
  })

  .case(getUrlInformation.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getPageSummary = (error as Response)
    })
  })
